import React, { useState } from 'react';
import { Modal, TextInput } from '@mantine/core';
import { formatChannelDesiredTime, resolveCibusPhase, takeAwayChannelLabel, type CibusStatusAction, type OrderView } from 'shared-models/browser';
import { Button } from '../../../../../components/Button';
import { formatMoney } from '../../../../../utils/money';
import { noSelectionTextInputProps } from '../../receipt/receipt-input.utils';
import { useCibusOrderActions } from './useCibusOrderActions';
import {
  cibusActionLabel,
  cibusDeliveryLabel,
  cibusShortId,
  cibusStatusLabel,
  isCibusMissing,
  visibleCibusActions,
} from './cibus-order.utils';
import classes from './cibus-order-panel.module.scss';

export type CibusOrderPanelProps = {
  tenantId: string;
  employeeSession: string | null;
  order: OrderView;
  // Accept succeeded: the parent prints this device's bon (+ receipt when closed).
  onAccepted?: (order: OrderView) => void;
  onDone?: () => void;
};

// Channel card for a Cibus package: header, meta line, mandated customer block, total, buttons.
export const CibusOrderPanel: React.FC<CibusOrderPanelProps> = ({ tenantId, employeeSession, order, onAccepted, onDone }) => {
  const { inFlight, apply } = useCibusOrderActions({ tenantId, employeeSession, orderId: order.id });
  const [pendingAction, setPendingAction] = useState<CibusStatusAction | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  if (!order.cibusPackageId) {
    return null;
  }

  const channelLabel = takeAwayChannelLabel(order.orderCode);
  const actions = visibleCibusActions(order.cibusStatus);
  const desiredTime = formatChannelDesiredTime(order.cibusDesiredTime);
  const isBusy = inFlight !== null;
  const phase = resolveCibusPhase(order.cibusStatus);
  const isVoidedButOpen = phase === 'canceled' && order.status === 'open';
  const pendingLabel = pendingAction ? cibusActionLabel(pendingAction, order.cibusDeliveryMethod) : '';

  const closeConfirm = () => {
    setPendingAction(null);
    setRejectReason('');
  };

  return (
    <div className={classes.panel} data-testid="cibus-order-panel">
      <div className={classes.header}>
        <span className={classes.brand}>{channelLabel}</span>
        <span className={classes.number} data-testid="cibus-order-number">#{cibusShortId(order.cibusPackageId)}</span>
        <span className={classes.badge} data-testid="cibus-order-status">{cibusStatusLabel(order.cibusStatus)}</span>
      </div>
      <div className={classes.meta}>
        {desiredTime && (
          <span className={classes.time} data-testid="cibus-order-desired-time">מועד: {desiredTime}</span>
        )}
        <span className={classes.chip} data-testid="cibus-order-delivery">{cibusDeliveryLabel(order.cibusDeliveryMethod)}</span>
        <span className={classes.chip} data-testid="cibus-order-payment">שולם בסיבוס</span>
      </div>
      {(order.guestName || order.cibusNote) && (
        <div className={classes.customer} data-testid="cibus-order-customer">
          {order.guestName && (
            <p className={classes.guest} data-testid="cibus-order-guest">{order.guestName}</p>
          )}
          {order.cibusNote && (
            <p className={classes.note} data-testid="cibus-order-note">{order.cibusNote}</p>
          )}
        </div>
      )}
      <div className={classes.total} data-testid="cibus-order-total">
        <span>סה"כ</span>
        <span>{formatMoney(Number(order.totalAmount ?? 0))}</span>
      </div>
      {isCibusMissing(order.cibusStatus) && (
        <p className={classes.hint} data-testid="cibus-order-missing-hint">החבילה נעלמה מסיבוס — ייתכן שבוטלה</p>
      )}
      {isVoidedButOpen && (
        <p className={classes.hint} data-testid="cibus-order-voided-hint">ההזמנה בוטלה ב{channelLabel} — יש לבטל את הפריטים ידנית</p>
      )}
      {actions.length > 0 && (
        <div className={classes.actions}>
          {actions.includes('accept') && (
            <Button variant="save" fullWidth onClick={() => setPendingAction('accept')} disabled={isBusy} loading={inFlight === 'accept'} dataTestId="cibus-order-accept">
              קבל
            </Button>
          )}
          {actions.includes('reject') && (
            <Button variant="cancel" fullWidth onClick={() => setPendingAction('reject')} disabled={isBusy} loading={inFlight === 'reject'} dataTestId="cibus-order-reject">
              דחה
            </Button>
          )}
          {actions.includes('sent') && (
            <Button
              variant="confirm"
              fullWidth
              onClick={() => setPendingAction('sent')}
              disabled={isBusy}
              loading={inFlight === 'sent'}
              dataTestId="cibus-order-sent"
            >
              {cibusActionLabel('sent', order.cibusDeliveryMethod)}
            </Button>
          )}
        </div>
      )}
      <Modal
        opened={pendingAction !== null}
        onClose={closeConfirm}
        title={`${pendingLabel} — הזמנת ${channelLabel}`}
        centered
        radius="md"
        zIndex={302}
        closeButtonProps={{ 'data-testid': 'cibus-order-confirm-close' } as Record<string, string>}
      >
        <div className={classes.modalBody} data-testid="cibus-order-confirm-modal">
          <p className={classes.modalText}>
            {pendingAction === 'accept' && `ההזמנה תישלח למטבח ותעודכן ב${channelLabel} כ"בהכנה".`}
            {pendingAction === 'reject' && `ההזמנה תבוטל ותעודכן ב${channelLabel} כנדחתה.`}
            {pendingAction === 'sent' && `ההזמנה תעודכן ב${channelLabel} כיצאה מהמסעדה.`}
          </p>
          {pendingAction === 'reject' && (
            <TextInput
              label="סיבת הדחייה (נשמרת אצלנו בלבד)"
              {...noSelectionTextInputProps}
              value={rejectReason}
              onChange={(event) => setRejectReason(event.currentTarget.value)}
              data-testid="cibus-order-reject-reason"
            />
          )}
          <div className={classes.modalActions}>
            <Button variant="cancel" fullWidth onClick={closeConfirm} disabled={isBusy} dataTestId="cibus-order-confirm-cancel">
              ביטול
            </Button>
            <Button
              variant="confirm"
              fullWidth
              loading={isBusy}
              onClick={async () => {
                if (!pendingAction) {
                  return;
                }
                const updated = await apply(pendingAction, pendingAction === 'reject' ? rejectReason.trim() || undefined : undefined);
                if (!updated) {
                  return;
                }
                closeConfirm();
                if (pendingAction === 'accept') {
                  onAccepted?.(updated);
                }
                onDone?.();
              }}
              dataTestId="cibus-order-confirm-submit"
            >
              {pendingLabel}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
